import styled from "@emotion/styled";
import { useLocation } from "react-router-dom";
import { trackEvent } from "contexts/mixpanel";
import { Button, FadeIn, FlexRow, IconX, Link, Text } from "atoms";
import { useAuth } from "features/auth";
import { useToggle } from "utils/hooks";

const trackDownload = (slug: string) =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Download",
    ButtonContext: "Resource",
    Resource: slug,
  });

const trackSignUp = (slug: string) => () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign Up",
    ButtonContext: `Resource`,
    Resource: slug,
  });

const trackSignIn = (slug: string) => () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign In",
    ButtonContext: "Resource",
    Resource: slug,
  });

type DownloadButtonProps = {
  slug: string;
  url: string;
};

function DownloadButton({ slug, url }: DownloadButtonProps) {
  const { user } = useAuth();
  const location = useLocation();

  const [isOpen, toggleIsOpen] = useToggle(false);

  if (!url) {
    return null;
  }

  if (!user) {
    return (
      <div>
        <SButton variant="secondary" size="xxs" onClick={toggleIsOpen}>
          Download
        </SButton>

        {isOpen && (
          <CardMenu aria-live="polite">
            <CardMenuClose variant="blank" onClick={toggleIsOpen}>
              <IconX aria-label="close share menu" />
            </CardMenuClose>

            <Text
              bold
              textAlign="center"
              textTransform="uppercase"
              font="display"
            >
              Create an account to download&nbsp;resources
            </Text>

            <FlexRow justifyContent="center" itemsFlex="0 0 auto" gap="10px">
              <Link
                variant="primary"
                size="xxs"
                to="/register"
                state={{ from: location }}
                onClick={trackSignUp(slug)}
              >
                Create Account
              </Link>

              <Link
                variant="primary"
                size="xxs"
                to="/sign-in"
                state={{ from: location }}
                onClick={trackSignIn(slug)}
              >
                Sign In
              </Link>
            </FlexRow>
          </CardMenu>
        )}
      </div>
    );
  }

  return (
    <div>
      <SLink
        variant="secondary"
        size="xxs"
        download
        href={url}
        onClick={() => trackDownload(slug)}
      >
        Download
      </SLink>
    </div>
  );
}

const CardMenu = styled(FadeIn)(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  border: "1px solid",
  borderColor: theme.colors.fg60,
  borderRadius: theme.borderRadius.sm,
  bottom: 10,
  boxShadow: theme.boxShadow.light,
  padding: "2rem 1rem",
  position: "absolute",
  left: "50%",
  width: "calc(100% - 20px)",
  transform: "translateX(-50%)",
  zIndex: 10,
}));

const CardMenuClose = styled(Button)(({ theme }) => ({
  height: 30,
  width: 30,
  borderRadius: theme.borderRadius.sm,
  overflow: "hidden",
  position: "absolute",
  right: 0,
  top: 0,
  fontSize: 12,
  opacity: 0.4,
  "&:hover, &:focus": {
    border: 0,
    opacity: 1,
  },
}));

const SButton = styled(Button)({
  position: "static",
  "&::after": {
    content: "''",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    position: "absolute",
    zIndex: 2,
  },
});
const SLink = styled(Link)({
  position: "static",
  "&::after": {
    content: "''",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    position: "absolute",
    zIndex: 2,
  },
});

export { DownloadButton };
